import { createContext, useEffect, useState, useCallback, use } from 'react';
import type { ReactNode } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';

interface CultureByPassageDataContextType {
  cultureByPassage: CultureByPassage[];
  getCultureByPassageByPassageId: (passageId: string) => CultureByPassage | undefined;
  loadCultureByPassageByPassageId: (passageId: string) => Promise<CultureByPassage | undefined>;
}

export const CultureByPassageDataContext = createContext<CultureByPassageDataContextType | undefined>(undefined);

interface CultureByPassageDataProviderProps {
  children: ReactNode;
  cultureByPassage: CultureByPassage[];
}

export function CultureByPassageDataProvider({ children, cultureByPassage: initialData }: CultureByPassageDataProviderProps) {
  const [loader] = useState(() => new LazyDataLoader<CultureByPassage>(DBCollection.cultureByPassage, 'passageId'));
  const [cachedItems, setCachedItems] = useState<CultureByPassage[]>([]);

  // Preload any initial data into memory cache
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      loader.preload(initialData);
      setCachedItems(loader.getAllCached());
    }
  }, [initialData, loader]);

  // Synchronous getter - returns from memory cache only
  const getCultureByPassageByPassageId = useCallback((passageId: string): CultureByPassage | undefined => {
    return cachedItems.find(item => item.passageId === passageId);
  }, [cachedItems]);

  // Async loader - fetches if not in memory, updates state when loaded
  const loadCultureByPassageByPassageId = useCallback(async (passageId: string): Promise<CultureByPassage | undefined> => {
    const item = await loader.get(passageId);
    if (item) {
      // Update cached items to trigger re-render
      setCachedItems(loader.getAllCached());
    }
    return item;
  }, [loader]);

  return (
    <CultureByPassageDataContext.Provider
      value={{
        cultureByPassage: cachedItems,
        getCultureByPassageByPassageId,
        loadCultureByPassageByPassageId
      }}
    >
      {children}
    </CultureByPassageDataContext.Provider>
  );
}

/**
 * Hook that auto-loads culture by passage data when accessed
 */
export function useCultureByPassageData(passageId?: string) {
  const context = use(CultureByPassageDataContext);
  const [data, setData] = useState<CultureByPassage | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!passageId || !context) return;

    // Check if already in memory
    const cached = context.getCultureByPassageByPassageId(passageId);
    if (cached) {
      setData(cached);
      return;
    }

    // Load from cache/firestore
    setIsLoading(true);
    context.loadCultureByPassageByPassageId(passageId)
      .then(loaded => {
        setData(loaded);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load culture by passage for ${passageId}:`, err);
        setIsLoading(false);
      });
  }, [passageId, context]);

  return { data, isLoading, context };
}

